import { KitType } from '@repo/types';
import { highRelativeAbundanceLimit } from 'assets';
import { Bacterium, DesignButton } from 'components';
import {
  initialValues,
  TaxonomyCategoryFilterType,
} from 'components/TaxonomyCategoryFilters';
import { AnimatePresence, motion } from 'framer-motion';
import { useMemo, useState } from 'react';

import { ButtonColors, ButtonVariants, Icon, Typography } from '@repo/ui';

import { TaxaData } from 'services/microbiome/types/taxa-data.types';
import styles from './Insights.module.scss';

export const InsightsTaxaList = ({
  data,
  type = KitType.STOOL,
  taxonomyFilters = initialValues,
}: {
  data: TaxaData[];
  type?: KitType;
  taxonomyFilters: TaxonomyCategoryFilterType[];
}) => {
  const [sortBy, setSortBy] = useState<'species' | 'abundance'>('abundance');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const sortedBacteriaList = useMemo(() => {
    const listToSort = [...data];
    listToSort.sort((a: TaxaData, b: TaxaData) => {
      const valA =
        sortBy === 'species'
          ? a.taxonomy_name
          : (a.classified_relative_abundance ?? 0);
      const valB =
        sortBy === 'species'
          ? b.taxonomy_name
          : (b.classified_relative_abundance ?? 0);

      let comparison = 0;
      if (sortBy === 'species') {
        comparison = (valA as string).localeCompare(valB as string);
      } else {
        comparison = (valA as number) - (valB as number);
      }
      if (isNaN(comparison)) comparison = 0;

      return sortDirection === 'asc' ? comparison : comparison * -1;
    });
    return listToSort;
  }, [data, sortBy, sortDirection]);

  const handleSort = (column: 'species' | 'abundance') => {
    if (column === sortBy) {
      setSortDirection(prevDirection =>
        prevDirection === 'asc' ? 'desc' : 'asc',
      );
    } else {
      setSortBy(column);
      setSortDirection(column === 'abundance' ? 'desc' : 'asc');
    }
  };

  const selectedFilter =
    taxonomyFilters.find(filter => filter.active === true)?.title || 'Species';

  const bacteriaListExpanded = sortedBacteriaList.filter(
    bacterium =>
      bacterium.classified_relative_abundance &&
      bacterium.classified_relative_abundance <= highRelativeAbundanceLimit,
  );

  const bacteriaListInitial = sortedBacteriaList.filter(
    bacterium =>
      bacterium.classified_relative_abundance &&
      bacterium.classified_relative_abundance > highRelativeAbundanceLimit,
  );

  return (
    <div className={styles.listContainer}>
      <div className={styles.listHeader}>
        <span
          className={styles.headerSpecies}
          onClick={() => handleSort('species')}
        >
          {selectedFilter}
          <Icon
            name={
              sortDirection === 'desc'
                ? 'triangleUpFilled'
                : 'triangleDownFilled'
            }
            size='xs'
          />
        </span>
        <span
          className={styles.headerAbundance}
          onClick={() => handleSort('abundance')}
        >
          Abundance
          <Icon
            name={
              sortDirection === 'asc'
                ? 'triangleUpFilled'
                : 'triangleDownFilled'
            }
            size='xs'
          />
        </span>
      </div>

      <AnimatePresence>
        {!sortedBacteriaList.length ? (
          <div className={styles.empty}>
            <Typography variant='label' serif>
              No species
            </Typography>
          </div>
        ) : (
          <>
            {bacteriaListInitial.map((bacterium, i) => (
              <Bacterium
                key={bacterium.rtc_id || i}
                bacterium={bacterium}
                type={type}
              />
            ))}
            <DesignButton
              label={
                isExpanded ? 'Hide low abundance microbes' : 'View all microbes'
              }
              iconNameRight={isExpanded ? 'chevronUp' : 'chevronDown'}
              width={'full'}
              color={ButtonColors.LIGHT}
              variant={ButtonVariants.SOLID}
              onClick={toggleExpand}
              className='mt-2'
            />
            {isExpanded && bacteriaListExpanded.length > 0 && (
              <motion.div
                key='expanded'
                initial={{ height: 0 }}
                animate={{ height: 'auto' }}
                exit={{ height: 0 }}
                transition={{ duration: 1 }}
                className={styles.collapse}
              >
                {bacteriaListExpanded.map((bacterium, i) => (
                  <Bacterium
                    key={bacterium.rtc_id || i}
                    bacterium={bacterium}
                    type={type}
                  />
                ))}
              </motion.div>
            )}
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
